import { useEffect, useRef } from 'react'

import { IncidentsFilterDrawerMode } from '../hooks/useDrawerMode'
import { useIncidentFilterFormValues } from '../hooks/useIncidentFilterFormValues'
import { FeatureFlag, useFeatureFlags } from '@/hooks/useFeatureFlags'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import { Box, DrawerBody, Flex, Spacer, useDisclosure } from '@chakra-ui/react'

import {
  Button,
  DateSelect,
  FormAccordionWrapper,
  FormInputSelectControl,
  FormMultiSelectControl,
  PageSpinner,
  RadioSelect,
} from '@/components/ui'
import { buildFilterCriteria } from '@/redux/incidents/conversion'
import {
  selectActiveFilter,
  setActiveFilterCriteria,
} from '@/redux/incidents/incidentsSlice'
import { hideScrollbarCss } from '@/styles/utils'
import { mixpanel } from '@/utils/analytics'
import { OptionI } from '@/utils/forms/optionBuilders'

import { ActiveFilterBadge } from '../ActiveFilterBadge'
import { IncidentFiltersFormInputsI } from '../types/form'
import { SavedFilterI } from '../types/generic'
import { removeEmptyFilters } from '../utils/utils'
import { CreateFilterModal } from './CreateFilterModal'
import { IncidentsFilterDrawerFooter } from './IncidentsFilterDrawerFooter'
import { UpdateFilterConfirmationModal } from './UpdateFilterConfirmationModal'

interface IncidentsFilterFormIProps {
  onClose: () => void
  onGoBack: () => void
  editedFilter: SavedFilterI | null
  drawerMode: IncidentsFilterDrawerMode
}

export const IncidentsFilterForm = ({
  onClose,
  onGoBack,
  editedFilter,
  drawerMode,
}: IncidentsFilterFormIProps) => {
  const dispatch = useDispatch()
  const activeFilter = useSelector(selectActiveFilter)

  const flags = useFeatureFlags()
  const isIncidentRulesEnabled = flags?.[FeatureFlag.incidentRules]
  const isIncidentTypeDefinition =
    flags?.[FeatureFlag.useIncidentTypeDefinition]
  const { control, handleSubmit, watch, formState, setValue, reset } =
    useForm<IncidentFiltersFormInputsI>({
      reValidateMode: 'onChange',
    })
  const lastDefaultValuesKeyRef = useRef<string | null>(null)

  const {
    defaultValues,
    options,
    deviceSearchValue,
    onDeviceSearchChange,
    facilitySearchValue,
    onFacilitySearchChange,
    isDeviceLoading,
    isFloorLoading,
    isFacilityLoading,
  } = useIncidentFilterFormValues({ watch, editedFilter })

  useEffect(() => {
    if (!defaultValues) return

    const defaultValuesKey = JSON.stringify(defaultValues)
    if (lastDefaultValuesKeyRef.current === defaultValuesKey) return

    reset(defaultValues)
    lastDefaultValuesKeyRef.current = defaultValuesKey
  }, [defaultValues, reset])

  const {
    isOpen: isModalOpen,
    onClose: onModalClose,
    onOpen: onModalOpen,
  } = useDisclosure()

  const onSubmit: SubmitHandler<IncidentFiltersFormInputsI> = async (
    values
  ) => {
    dispatch(
      setActiveFilterCriteria(
        buildFilterCriteria(values, isIncidentTypeDefinition)
      )
    )
    onClose()
    mixpanel.track('Incidents View: Filters Applied', {
      filters: removeEmptyFilters(values),
    })
  }

  const mutedStatusFilterCount = watch('mutedStatus') == null ? null : 1
  const createdAtFilterCount = watch('createdAt') == null ? null : 1
  const clipsFilterCount = watch('clips') == null ? null : 1
  const aiOperatorStateFilterCount = watch('aiOperatorState') == null ? null : 1

  if (defaultValues === null) {
    return <PageSpinner />
  }

  return (
    <>
      <DrawerBody css={hideScrollbarCss}>
        <Flex justifyContent='space-between' mb='3' mt='6' w='100%'>
          {activeFilter ? (
            <ActiveFilterBadge label={activeFilter?.name} />
          ) : (
            <Spacer />
          )}
          <Button
            isDisabled={editedFilter ? !formState.isDirty : false}
            onClick={onModalOpen}
            style={{
              height: '40px',
              paddingLeft: '20px',
              paddingRight: '20px',
              width: '120px',
              cursor: 'pointer',
            }}
          >
            Save Filter
          </Button>
        </Flex>
        <Box mb='3'>
          <FormMultiSelectControl
            control={control}
            defaultValue={defaultValues.facilityName}
            id='facilityName'
            isLoading={isFacilityLoading}
            label='Facility Name'
            labelStyle={{ cursor: 'inherit' }}
            onSearchChange={onFacilitySearchChange}
            options={options.facilityNameOptions}
            placeholder='Select Facility(-ies)'
            searchValue={facilitySearchValue}
            shouldSortOptions={false}
          />
        </Box>
        <Box mb='3'>
          <FormMultiSelectControl
            control={control}
            defaultValue={defaultValues.floorName}
            id='floorName'
            isLoading={isFloorLoading}
            label='Floor Name'
            labelStyle={{ cursor: 'inherit' }}
            options={options.floorNameOptions}
            placeholder='Select Floor(s)'
          />
        </Box>
        <Box mb='3'>
          <FormMultiSelectControl
            control={control}
            defaultValue={defaultValues.deviceName}
            id='deviceName'
            isLoading={isDeviceLoading}
            label='Device Name'
            labelStyle={{ cursor: 'inherit' }}
            onSearchChange={onDeviceSearchChange}
            options={options.deviceNameOptions}
            placeholder={
              onDeviceSearchChange === null
                ? 'Select Device(s)'
                : 'Search Device(s)'
            }
            searchValue={deviceSearchValue}
          />
        </Box>
        <Box mb='3'>
          <FormAccordionWrapper
            label='Created At'
            labelStyle={{ cursor: 'inherit' }}
            numberSelected={createdAtFilterCount}
          >
            <DateSelect
              control={control}
              defaultValue={defaultValues.createdAt}
              id='createdAt'
              options={options.createdAtOptions}
            />
          </FormAccordionWrapper>
        </Box>
        <Box mb={3}>
          <FormMultiSelectControl
            control={control}
            defaultValue={defaultValues.incidentStatus}
            id='incidentStatus'
            label='Incident Status'
            labelStyle={{ cursor: 'inherit' }}
            options={options.incidentStatusOptions}
            placeholder='Select Incident Status(-es)'
          />
        </Box>
        <Box mb={3}>
          <FormMultiSelectControl
            control={control}
            defaultValue={defaultValues.sourceType}
            id='sourceType'
            label='Source Type'
            labelStyle={{ cursor: 'inherit' }}
            options={options.sourceTypeOptions}
            placeholder='Select Source Type'
          />
        </Box>
        <Box mb={3}>
          <FormMultiSelectControl
            control={control}
            defaultValue={defaultValues.incidentType}
            id='incidentType'
            label='Incident Type'
            labelStyle={{ cursor: 'inherit' }}
            options={options.incidentTypeOptions}
            placeholder='Select Incident Type'
          />
        </Box>
        <Box mb={3}>
          <FormMultiSelectControl
            control={control}
            defaultValue={
              defaultValues.priority as unknown as OptionI<string>[]
            }
            id='priority'
            label='Priority'
            labelStyle={{ cursor: 'inherit' }}
            options={options.priorityOptions as unknown as OptionI<string>[]}
            placeholder='Select Priority(-ies)'
            shouldSortOptions={false}
          />
        </Box>
        {isIncidentRulesEnabled && (
          <Box mb={3}>
            <FormAccordionWrapper
              label='Rule Action'
              numberSelected={mutedStatusFilterCount}
            >
              <FormInputSelectControl
                control={control}
                dataTestId='incidentsPage_filterDrawer_mute'
                defaultValue={defaultValues.mutedStatus}
                id='mutedStatus'
                isSearchable
                options={options.mutedOptions}
                placeholder='Selected muted or alarmed by scheduled rules'
              />
            </FormAccordionWrapper>
          </Box>
        )}

        <Box mb={3}>
          <FormAccordionWrapper label='Clips' numberSelected={clipsFilterCount}>
            <FormInputSelectControl
              control={control}
              dataTestId='incidentsPage_filterDrawer_clips'
              defaultValue={defaultValues.clips}
              id='clips'
              isClearable
              isSearchable
              options={options.clipsOptions}
              placeholder='Select clips filter'
            />
          </FormAccordionWrapper>
        </Box>

        <Box mb='3'>
          <FormAccordionWrapper
            label='AI Operator'
            labelStyle={{ cursor: 'inherit' }}
            numberSelected={aiOperatorStateFilterCount}
          >
            <RadioSelect
              control={control}
              defaultValue={defaultValues.aiOperatorState}
              id='aiOperatorState'
              options={options.aiOperatorStateOptions}
            />
          </FormAccordionWrapper>
        </Box>
      </DrawerBody>
      {drawerMode === IncidentsFilterDrawerMode.DETAIL && (
        <IncidentsFilterDrawerFooter
          activeFilter={activeFilter}
          onApplyFilters={handleSubmit(onSubmit)}
          setValue={setValue}
        />
      )}
      {isModalOpen &&
        (drawerMode === IncidentsFilterDrawerMode.UPDATE ? (
          <UpdateFilterConfirmationModal
            editedFilter={editedFilter}
            incidentFiltersFormValues={watch()}
            isOpen={isModalOpen}
            onClose={() => {
              onModalClose()
              onGoBack()
            }}
          />
        ) : (
          <CreateFilterModal
            incidentFiltersFormValues={watch()}
            isOpen={isModalOpen}
            onClose={onModalClose}
          />
        ))}
    </>
  )
}
